import {
  useEffect,
  useRef,
  type ReactElement,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react";
import { Link } from "react-router";
import { routePathById } from "@/app/router/route-manifest";
import { Alert, AlertDescription, AlertTitle, Button } from "@/shared/components/ui";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { SupportReference } from "@/shared/components/support-reference";
import type { MemberInvoicesApiError } from "./api/member-invoices";

/** DES-1 auth-surface heading: centered, 2xl, light, dark gray. */
export const PUBLIC_HEADING_CLASS =
  "text-center text-2xl font-light text-clm-dark-gray focus:outline-none";
/** DES-1 auth-surface action column: centered, primary above secondary. */
export const PUBLIC_ACTIONS_CLASS = "mt-6 flex flex-col items-center gap-2";

/**
 * Shared 006-9 surface states (UI contract §5.1, §7, §10). Copy is verbatim
 * from the contract; the API layer already collapsed provider/transport
 * detail into `MemberInvoicesApiError.kind`, so nothing here echoes messages.
 */

export const SUPPORT_ACTION_LABEL = "Contact support";
/** The existing support entry (UI contract §4/§5.1; same target as the footer "Contact" link). */
export const SUPPORT_PATH = "/home/contactus";
export const RETRY_ACTION_LABEL = "Try again";
/** §10 Resolving: one label for the locator resolve/continue and the §5.1 prepare while in flight. */
export const RESOLVING_LABEL = "Opening your invoice…";
export const VIEW_INVOICES_ACTION_LABEL = "View my invoices";
export const OPEN_INVOICES_PATH = routePathById("pay-invoices");
/** §5.1/§6.2 transient trouble (503, rate limit, network, provider), as title + body. */
export const TEMPORARILY_UNAVAILABLE_TITLE = "Payment is temporarily unavailable";
export const TEMPORARILY_UNAVAILABLE_BODY = "Please try again in a moment.";

/**
 * Ordinary login with `path` as the return route (UI contract §4, §5.1) — the
 * manifest `login` route with the same `returnTo` query `ProtectedRoute` emits.
 */
export function loginReturnPath(path: string): string {
  return `${routePathById("login")}?returnTo=${encodeURIComponent(path)}`;
}
/** §4/§7: sign in and land on the open-invoice list. */
export const OPEN_INVOICES_LOGIN_PATH = loginReturnPath(OPEN_INVOICES_PATH);

/** §10 notice panel announced above a card body (e.g. §5.4 stale copy). */
export function NoticeAlert({
  children,
  ref,
}: {
  children: ReactNode;
  /** §10 Focus — when provided the panel is a programmatic focus target. */
  ref?: Ref<HTMLParagraphElement>;
}): ReactElement {
  return (
    <p
      ref={ref}
      tabIndex={ref ? -1 : undefined}
      role="alert"
      className="mb-4 rounded-md bg-clm-light-gray/40 p-3 text-sm"
    >
      {children}
    </p>
  );
}

/**
 * §10 Focus — every new surface moves focus to its `h1`. No shared autofocus
 * consumer exists in the SPA, so the surface focuses its own heading on mount.
 * Surfaces that swap their content in place (the status card moving from
 * "Payment pending" to "Invoice paid") pass the heading as `key` so the new
 * surface refocuses even though the component instance is reused.
 */
export function useHeadingFocus(key?: unknown): RefObject<HTMLHeadingElement | null> {
  const ref = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    ref.current?.focus();
  }, [key]);
  return ref;
}

export function InvoicesLoading({ label }: { label: string }): ReactElement {
  return (
    <div
      role="status"
      aria-busy="true"
      className="flex items-center justify-center gap-3 py-12 text-muted-foreground"
    >
      <ClmIcon name="loading" decorative className="h-6 w-6 animate-spin text-clm-brand-blue" />
      <span>{label}</span>
    </div>
  );
}

/**
 * §10 Loading / Resolving inline cue: the shared `loading` glyph + a short
 * label. Callers place it inside their own `role="status"` region and own
 * that region's `aria-busy`, so the in-flight text is announced once.
 */
export function BusyCue({ label }: { label: string }): ReactElement {
  return (
    <span className="flex items-center gap-2">
      <ClmIcon name="loading" decorative className="h-4 w-4 animate-spin text-clm-brand-blue" />
      <span>{label}</span>
    </span>
  );
}

/** §10 Keyboard: every action is a link or button in reading order. */
export function ContactSupportAction(): ReactElement {
  return (
    <Button asChild variant="secondary">
      <Link to={SUPPORT_PATH} data-testid="contact-support-action">
        {SUPPORT_ACTION_LABEL}
      </Link>
    </Button>
  );
}

export function InvoicesUnavailable({
  title,
  body,
  correlationId,
  actions,
  presentation = "app",
}: {
  title: string;
  body: string;
  correlationId?: string | null;
  actions: ReactNode;
  /** `public`: DES-1 auth-surface heading and centered action column (public `/pay` entry). */
  presentation?: "app" | "public";
}): ReactElement {
  const headingRef = useHeadingFocus();
  const isPublic = presentation === "public";
  return (
    // Public: the PayPublicChrome card is the frame, so the Alert box and its
    // title/body typography give way to the guidance-page presentation.
    <Alert className={isPublic ? "border-0 bg-transparent p-0 text-base" : undefined}>
      <AlertTitle
        as="h1"
        ref={headingRef}
        tabIndex={-1}
        className={isPublic ? `${PUBLIC_HEADING_CLASS} mb-0 leading-8 tracking-normal` : undefined}
      >
        {title}
      </AlertTitle>
      <AlertDescription className={isPublic ? "[&_p]:leading-normal" : undefined}>
        <p className={isPublic ? "mt-2 text-base text-gray-600" : undefined}>{body}</p>
        <SupportReference correlationId={correlationId ?? undefined} />
        <div className={isPublic ? PUBLIC_ACTIONS_CLASS : "mt-4 flex flex-wrap gap-2"}>
          {actions}
        </div>
      </AlertDescription>
    </Alert>
  );
}

export function TemporarilyUnavailable({
  error,
  onRetry,
  supportAction = false,
  presentation,
}: {
  /** Absent when no server answer exists (e.g. a missing local payment configuration). */
  error?: MemberInvoicesApiError | null;
  onRetry: () => void;
  /** Offer the support path beside the retry (public entry surfaces). */
  supportAction?: boolean;
  presentation?: "app" | "public";
}): ReactElement {
  return (
    <InvoicesUnavailable
      title={TEMPORARILY_UNAVAILABLE_TITLE}
      body={TEMPORARILY_UNAVAILABLE_BODY}
      correlationId={error?.correlationId ?? null}
      presentation={presentation}
      actions={
        <>
          <Button
            type="button"
            onClick={onRetry}
            className={presentation === "public" ? "w-full" : undefined}
          >
            {RETRY_ACTION_LABEL}
          </Button>
          {supportAction ? <ContactSupportAction /> : null}
        </>
      }
    />
  );
}

export function ViewInvoicesAction({ className }: { className?: string } = {}): ReactElement {
  return (
    <Button asChild className={className}>
      <Link to={OPEN_INVOICES_PATH}>{VIEW_INVOICES_ACTION_LABEL}</Link>
    </Button>
  );
}
